import { Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useAlertView } from '~/app';

const TAGS = CT.alert;
type AlertFlavor = Record<
  'error' | 'success' | 'info' | 'warning' | 'plain',
  {
    icon?: typeof XCircleIcon;
    bgColor: string;
    iconColor?: string;
    textColor: string;
    subTextColor: string;
    closeBtn: string;
    accentBorder: string;
  }
>;

const FLAVORS: AlertFlavor = {
  error: {
    icon: XCircleIcon,
    bgColor: 'bg-error-t-900',
    iconColor: 'text-error-50',
    textColor: 'text-error-50',
    subTextColor: 'text-error-t-200',
    closeBtn: 'text-error-t-200 hover:text-error-50 hover:bg-error-t-800',
    accentBorder: 'border-l-4 border-error-50',
  },
  success: {
    icon: CheckCircleIcon,
    bgColor: 'bg-success-subtle',
    iconColor: 'text-good',
    textColor: 'text-grey-100',
    subTextColor: 'text-grey-300',
    closeBtn: 'text-grey-400 hover:text-grey-100 hover:bg-success/10',
    accentBorder: 'border-l-4 border-good',
  },
  info: {
    icon: InformationCircleIcon,
    bgColor: 'bg-primary-t-900',
    iconColor: 'text-primary-50',
    textColor: 'text-primary-50',
    subTextColor: 'text-primary-t-200',
    closeBtn: 'text-primary-t-300 hover:text-primary-50 hover:bg-primary-t-800',
    accentBorder: 'border-l-4 border-primary-50',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bgColor: 'bg-warning-50',
    iconColor: 'text-warning-600',
    textColor: 'text-grey-100',
    subTextColor: 'text-grey-300',
    closeBtn: 'text-grey-400 hover:text-grey-100 hover:bg-warning-100',
    accentBorder: 'border-l-4 border-warning',
  },
  plain: {
    bgColor: 'bg-white',
    textColor: 'text-grey-100',
    subTextColor: 'text-grey-400',
    closeBtn: 'text-grey-400 hover:text-grey-100 hover:bg-grey-900',
    accentBorder: 'border-l-4 border-grey-800',
  },
};

const Alert = () => {
  const alertView = useAlertView();
  const { activeAlert } = useSelector(alertView, (state) => state.context);

  const handleClose = () => {
    if (activeAlert) {
      alertView.send({ type: 'ON_DISMISS_ALERT', payload: { id: activeAlert.id } });
    }
  };

  // Don't render if no active alert
  if (!activeAlert) {
    return null;
  }

  const flavor = FLAVORS[activeAlert.type];
  const Icon = flavor.icon;

  // Portal to document.body so the alert is a direct child of <body>.
  // HeadlessUI's outside-click detection treats direct children of <body>
  // as "allowed" containers, preventing clicks on the alert from closing
  // any open Dialog behind it.
  return createPortal(
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-60 flex items-end px-4 py-10 sm:items-start sm:p-6"
      data-cy={TAGS.layout}
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition show={!!activeAlert}>
          <div
            className={classNames(
              'pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl',
              'shadow-xl ring-1 ring-black/5 transition data-closed:opacity-0',
              'data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2',
              'data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2',
              'data-closed:data-enter:sm:translate-y-0',
              flavor.bgColor,
              flavor.accentBorder
            )}
          >
            <div className="p-4" data-cy={activeAlert.cyTag}>
              <div className="flex items-start">
                {Icon && (
                  <div className="shrink-0">
                    <Icon aria-hidden="true" className={classNames('size-5', flavor.iconColor)} />
                  </div>
                )}
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  {activeAlert.text && (
                    <p
                      data-cy={TAGS.text}
                      className={classNames('text-sm font-semibold', flavor.textColor)}
                    >
                      {activeAlert.text}
                    </p>
                  )}
                  {activeAlert.subText && (
                    <p
                      data-cy={TAGS.subText}
                      className={classNames('mt-1 text-sm', flavor.subTextColor)}
                    >
                      {activeAlert.subText}
                    </p>
                  )}
                  {activeAlert.actions && activeAlert.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {activeAlert.actions.map((action) => (
                        <button
                          key={action.label}
                          type="button"
                          data-cy={action.cyTag}
                          onClick={() => {
                            handleClose();
                            action.onClick();
                          }}
                          className={classNames(
                            'cursor-pointer rounded-md px-2 py-1 text-sm font-semibold underline underline-offset-2',
                            flavor.textColor
                          )}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className={classNames(
                    'inline-flex cursor-pointer rounded-full p-1 transition-colors',
                    flavor.closeBtn
                  )}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon aria-hidden="true" className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>,
    document.body
  );
};

export default Alert;
